import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';

const CryptoBubbleCanvas = ({ data }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const draggingNode = useRef(null);
    const mousePos = useRef({ x: 0, y: 0 });

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        let width = canvas.width = window.innerWidth;
        let height = canvas.height = window.innerHeight;

        const resizeCanvas = () => {
            width = canvas.width = window.innerWidth;
            height = canvas.height = window.innerHeight;
            simulation.force('center', d3.forceCenter(width / 2, height / 2));
        };

        window.addEventListener('resize', resizeCanvas);

        data.forEach(d => {
            d.pulseOffset = Math.random() * 1000;
        });

        const simulation = d3.forceSimulation(data)
            .force('charge', d3.forceManyBody().strength(5))
            .force('center', d3.forceCenter(width / 2, height / 2))
            .force('collision', d3.forceCollide().radius(d => d.radius + 10))
            .alphaDecay(0.005)
            .on('tick', () => {
                data.forEach(d => {
                    const r = d.radius;
                    d.x = Math.max(r, Math.min(width - r, d.x));
                    d.y = Math.max(r, Math.min(height - r, d.y));
                });
            });

        const hexToRgba = (hex, alpha) => {
            const bigint = parseInt(hex.slice(1), 16);
            const r = (bigint >> 16) & 255;
            const g = (bigint >> 8) & 255;
            const b = bigint & 255;
            return `rgba(${r},${g},${b},${alpha})`;
        };

        const images = {};
        data.forEach(d => {
            const img = new Image();
            img.src = d.logo;
            images[d.id] = img;
        });

        const draw = (time) => {
            ctx.clearRect(0, 0, width, height);

            data.forEach(d => {
                if (draggingNode.current === d) {
                    d.fx += (mousePos.current.x - d.fx) * 0.005;
                    d.fy += (mousePos.current.y - d.fy) * 0.005;
                }

                const pulse = Math.sin((time + d.pulseOffset) / 300) * 2;
                const r = d.radius + pulse;

                ctx.beginPath();
                ctx.arc(d.x, d.y, r, 0, 2 * Math.PI);

                const gradient = ctx.createRadialGradient(d.x, d.y, 0, d.x, d.y, r);
                gradient.addColorStop(0, hexToRgba(d.color, 0.1));
                gradient.addColorStop(0.8, hexToRgba(d.color, 0.1));
                gradient.addColorStop(0.85, hexToRgba(d.color, 0.2));
                gradient.addColorStop(0.9, hexToRgba(d.color, 0.4));
                gradient.addColorStop(0.95, hexToRgba(d.color, 0.6));
                gradient.addColorStop(1, hexToRgba(d.color, 1));

                ctx.fillStyle = gradient;
                ctx.fill();

                const img = images[d.id];
                if (img && img.complete) {
                    const imgSize = r * 0.8;
                    const imgX = d.x - imgSize / 2;
                    const imgY = d.y - imgSize * 0.9;

                    ctx.save();
                    ctx.beginPath();
                    ctx.arc(d.x, d.y - imgSize * 0.4, imgSize / 2, 0, 2 * Math.PI);
                    ctx.clip();
                    ctx.drawImage(img, imgX, imgY, imgSize, imgSize);
                    ctx.restore();
                }

                ctx.fillStyle = '#fff';
                ctx.font = `bold ${r * 0.25}px sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(d.name, d.x, d.y + r * 0.4);
            });

            animationRef.current = requestAnimationFrame(draw);
        };

        animationRef.current = requestAnimationFrame(draw);

        // Interaction
        let isDragging = false;
        let clickStartTime = 0;

        const getEventPos = (e) => {
            const rect = canvas.getBoundingClientRect();
            if (e.touches && e.touches.length > 0) {
                return {
                    x: e.touches[0].clientX - rect.left,
                    y: e.touches[0].clientY - rect.top
                };
            }
            return {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            };
        };

        const findNodeAt = (x, y) => {
            return data.find(d => {
                const dx = x - d.x;
                const dy = y - d.y;
                return Math.sqrt(dx * dx + dy * dy) < d.radius;
            });
        };

        const handleStart = (e) => {
            const { x, y } = getEventPos(e);
            const node = findNodeAt(x, y);
            if (node) {
                clickStartTime = Date.now();
                draggingNode.current = node;
                node.fx = node.x;
                node.fy = node.y;
                mousePos.current = { x, y };
                isDragging = true;
                simulation.alphaTarget(0.3).restart();
            }
        };

        const handleMove = (e) => {
            if (!isDragging || !draggingNode.current) return;
            const { x, y } = getEventPos(e);

            const clampedX = Math.max(draggingNode.current.radius, Math.min(x, canvas.width - draggingNode.current.radius));
            const clampedY = Math.max(draggingNode.current.radius, Math.min(y, canvas.height - draggingNode.current.radius));

            mousePos.current = { x: clampedX, y: clampedY };
        };

        const handleEnd = () => {
            if (draggingNode.current) {
                const timeDiff = Date.now() - clickStartTime;
                if (timeDiff < 250 && draggingNode.current.link) {
                    window.open(draggingNode.current.link, '_blank');
                }
                draggingNode.current.fx = null;
                draggingNode.current.fy = null;
                draggingNode.current = null;
                simulation.alphaTarget(0);
            }
            isDragging = false;
        };

        canvas.addEventListener('mousedown', handleStart);
        canvas.addEventListener('mousemove', handleMove);
        canvas.addEventListener('mouseup', handleEnd);
        canvas.addEventListener('mouseleave', handleEnd);

        canvas.addEventListener('touchstart', handleStart, { passive: false });
        canvas.addEventListener('touchmove', handleMove, { passive: false });
        canvas.addEventListener('touchend', handleEnd);

        return () => {
            simulation.stop();
            cancelAnimationFrame(animationRef.current);
            window.removeEventListener('resize', resizeCanvas);

            canvas.removeEventListener('mousedown', handleStart);
            canvas.removeEventListener('mousemove', handleMove);
            canvas.removeEventListener('mouseup', handleEnd);
            canvas.removeEventListener('mouseleave', handleEnd);

            canvas.removeEventListener('touchstart', handleStart);
            canvas.removeEventListener('touchmove', handleMove);
            canvas.removeEventListener('touchend', handleEnd);
        };
    }, [data]);

    return <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100vh' }} />;
};

export default CryptoBubbleCanvas;
